import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Loader2, Sparkles, Copy, RefreshCw, Linkedin, Layers, FileText, Video } from "lucide-react";
import { toast } from "sonner";

function copyText(text) {
  try {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied to clipboard"),
      () => toast.success("Copied to clipboard")
    );
  } catch {
    toast.success("Copied to clipboard");
  }
}

function carouselToText(carousel, scopedUrl) {
  if (!carousel) return "";
  const slides = (carousel.slides || [])
    .map((s) => `Slide ${s.n} — ${s.headline}\n${s.body}`)
    .join("\n\n");
  return `${carousel.title || "Carousel"}\n\n${slides}\n\n— Caption —\n${carousel.caption || scopedUrl}`;
}

function videoToText(v, scopedUrl) {
  if (!v) return "";
  const beats = (v.beats || []).map((b) => `[${b.t}] ${b.section}: ${b.line}`).join("\n");
  return `${v.title || "60-second video"} (${v.duration_seconds || 60}s)\n\n${beats}\n\nOn-screen CTA: ${v.on_screen_cta || scopedUrl}`;
}

export default function LinkedInAssetsDialog({ open, link, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [assets, setAssets] = useState(null);
  const [scopedUrl, setScopedUrl] = useState("");
  const [generatedAt, setGeneratedAt] = useState(null);

  useEffect(() => {
    if (!open || !link) return;
    setLoading(true);
    setAssets(null);
    api
      .get(`/consultants/me/share-links/${link.id}/linkedin-assets`)
      .then((r) => {
        setAssets(r.data.assets || null);
        setScopedUrl(r.data.scoped_url || "");
        setGeneratedAt(r.data.generated_at || null);
      })
      .catch(() => toast.error("Failed to load LinkedIn assets"))
      .finally(() => setLoading(false));
  }, [open, link]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/consultants/me/share-links/${link.id}/linkedin-assets`);
      setAssets(data.assets);
      setScopedUrl(data.scoped_url);
      setGeneratedAt(data.generated_at);
      toast.success("LinkedIn assets generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const carousel = assets?.carousel;
  const textPost = assets?.text_post;
  const video = assets?.video_script;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="linkedin-assets-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Linkedin className="w-5 h-5 text-accent" /> LinkedIn assets
          </DialogTitle>
          <DialogDescription>
            {link ? (
              <>Tailored for <span className="text-foreground">{link.country}</span> · <span className="text-foreground">{link.industry}</span> · <span className="text-foreground">{link.function}</span></>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex flex-col items-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin text-accent" />
            <div className="mt-3 text-sm">Loading…</div>
          </div>
        ) : !assets ? (
          <div className="mt-4 border border-dashed border-border/60 rounded-md p-10 text-center" data-testid="linkedin-empty">
            <Sparkles className="w-7 h-7 text-accent mx-auto" />
            <h3 className="font-sans-ui font-semibold mt-4">Generate LinkedIn assets</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              We&rsquo;ll draft a 5-slide carousel, a single-image text post, and a 60-second video script — each ending with your scoped link as the CTA.
            </p>
            <Button
              onClick={generate}
              disabled={generating}
              data-testid="linkedin-generate-btn"
              className="mt-6 bg-accent text-accent-foreground hover:bg-accent/90 rounded-full"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {generating ? "Generating…" : "Generate assets"}
            </Button>
          </div>
        ) : (
          <div className="mt-2">
            <div className="flex items-center justify-between flex-wrap gap-2 border-b border-border/60 pb-3 mb-4">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                {generatedAt ? `Generated ${new Date(generatedAt).toLocaleString()}` : ""}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={generate}
                disabled={generating}
                data-testid="linkedin-regen-btn"
              >
                {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                Regenerate
              </Button>
            </div>

            <Tabs defaultValue="carousel">
              <TabsList className="bg-card/60">
                <TabsTrigger value="carousel" data-testid="linkedin-tab-carousel"><Layers className="w-3.5 h-3.5 mr-1.5" /> Carousel</TabsTrigger>
                <TabsTrigger value="post" data-testid="linkedin-tab-post"><FileText className="w-3.5 h-3.5 mr-1.5" /> Text post</TabsTrigger>
                <TabsTrigger value="video" data-testid="linkedin-tab-video"><Video className="w-3.5 h-3.5 mr-1.5" /> Video script</TabsTrigger>
              </TabsList>

              <TabsContent value="carousel" className="mt-4">
                <Card className="card-elevated p-5" data-testid="linkedin-carousel-card">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="overline">5-slide carousel</div>
                      <h4 className="font-sans-ui font-semibold mt-1">{carousel?.title}</h4>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => copyText(carouselToText(carousel, scopedUrl))} data-testid="linkedin-carousel-copy">
                      <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                    </Button>
                  </div>
                  <div className="mt-4 grid gap-3">
                    {(carousel?.slides || []).map((s) => (
                      <div key={s.n} className="border border-border/60 rounded-md p-3" data-testid={`linkedin-slide-${s.n}`}>
                        <div className="text-[10px] uppercase tracking-widest text-accent">Slide {s.n}</div>
                        <div className="font-sans-ui font-semibold mt-1">{s.headline}</div>
                        <div className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{s.body}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 border-t border-border/60 pt-4">
                    <div className="overline">Post caption</div>
                    <pre className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap font-body" data-testid="linkedin-carousel-caption">
{carousel?.caption}
                    </pre>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="post" className="mt-4">
                <Card className="card-elevated p-5" data-testid="linkedin-post-card">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="overline">Single-image text post</div>
                      <h4 className="font-sans-ui font-semibold mt-1">
                        {(textPost || "").length} chars
                        {(textPost || "").length > 1300 ? <span className="ml-2 text-xs text-accent">(over LinkedIn&apos;s 1300-char fold)</span> : null}
                      </h4>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => copyText(textPost || "")} data-testid="linkedin-post-copy">
                      <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                    </Button>
                  </div>
                  <pre className="mt-4 text-sm text-muted-foreground whitespace-pre-wrap font-body bg-secondary/40 border border-border/60 rounded-md p-4" data-testid="linkedin-post-body">
{textPost}
                  </pre>
                </Card>
              </TabsContent>

              <TabsContent value="video" className="mt-4">
                <Card className="card-elevated p-5" data-testid="linkedin-video-card">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="overline">60-second video script</div>
                      <h4 className="font-sans-ui font-semibold mt-1">{video?.title}</h4>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => copyText(videoToText(video, scopedUrl))} data-testid="linkedin-video-copy">
                      <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                    </Button>
                  </div>
                  <div className="mt-4 grid gap-2">
                    {(video?.beats || []).map((b, i) => (
                      <div key={`beat-${b.t || i}-${i}`} className="border border-border/60 rounded-md p-3 flex gap-3" data-testid={`linkedin-beat-${i}`}>
                        <div className="shrink-0 w-20">
                          <div className="text-[10px] uppercase tracking-widest text-accent">{b.t}</div>
                          <div className="text-xs text-muted-foreground">{b.section}</div>
                        </div>
                        <div className="text-sm text-foreground leading-relaxed">{b.line}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 rounded-md bg-accent/10 border border-accent/30 text-sm" data-testid="linkedin-video-cta">
                    <span className="overline">On-screen CTA</span>
                    <div className="mt-1 text-foreground">{video?.on_screen_cta}</div>
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
